/* Wendle theme — interactions. No dependencies. */
(() => {
  'use strict';
  const W = window.Wendle || {};
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));

  /* ---------- Money ---------- */
  const formatMoney = (cents) => {
    const fmt = W.moneyFormat || '£{{amount}}';
    const value = (cents / 100);
    const withDelims = (n, dec, t, d) => {
      const parts = n.toFixed(dec).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, t);
      return parts.join(d);
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return withDelims(value, 0, ',', '.');
        case 'amount_with_comma_separator': return withDelims(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelims(value, 0, '.', ',');
        default: return withDelims(value, 2, ',', '.');
      }
    });
  };

  /* ---------- Toast ---------- */
  let toastTimer;
  const toast = (msg) => {
    const el = $('#Toast');
    if (!el) return;
    el.textContent = msg;
    el.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => { el.hidden = true; }, 3200);
  };

  /* ---------- Drawers (menu, cart, filters, size guide) ---------- */
  let lastFocus = null;
  const focusables = (root) => $$('a[href],button:not([disabled]),input:not([disabled]):not([type=hidden]),select:not([disabled]),textarea,[tabindex]:not([tabindex="-1"])', root).filter((el) => el.offsetParent !== null);

  const openDrawer = (id) => {
    const d = document.getElementById(id);
    if (!d) return;
    lastFocus = document.activeElement;
    d.hidden = false;
    requestAnimationFrame(() => requestAnimationFrame(() => d.classList.add('is-open')));
    document.body.classList.add('has-drawer');
    const f = focusables($('.drawer__panel', d));
    setTimeout(() => (f[0] || d).focus({ preventScroll: true }), 60);
    if (id === 'FilterDrawer') moveFacets(true);
  };
  const closeDrawer = (d) => {
    if (!d || d.hidden) return;
    d.classList.remove('is-open');
    document.body.classList.remove('has-drawer');
    setTimeout(() => { d.hidden = true; if (d.id === 'FilterDrawer') moveFacets(false); }, 320);
    if (lastFocus) lastFocus.focus({ preventScroll: true });
  };
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) { e.preventDefault(); openDrawer(opener.dataset.drawerOpen); return; }
    const closer = e.target.closest('[data-drawer-close]');
    if (closer) { closeDrawer(closer.closest('[data-drawer]')); }
  });
  document.addEventListener('keydown', (e) => {
    const open = $$('[data-drawer].is-open').pop();
    if (!open) return;
    if (e.key === 'Escape') { closeDrawer(open); return; }
    if (e.key === 'Tab') {
      const f = focusables($('.drawer__panel', open));
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });

  /* ---------- Announcement rotation ---------- */
  $$('[data-announce]').forEach((bar) => {
    const items = $$('.announce__item', bar);
    if (items.length < 2 || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let i = 0, paused = false;
    bar.addEventListener('mouseenter', () => { paused = true; });
    bar.addEventListener('mouseleave', () => { paused = false; });
    bar.addEventListener('focusin', () => { paused = true; });
    bar.addEventListener('focusout', () => { paused = false; });
    setInterval(() => {
      if (paused || document.hidden) return;
      items[i].classList.remove('is-active');
      i = (i + 1) % items.length;
      items[i].classList.add('is-active');
    }, 4500);
  });

  /* ---------- Desktop navigation ---------- */
  const closeNav = () => $$('.nav__item.is-open').forEach((li) => { li.classList.remove('is-open'); $('[data-nav-toggle]', li).setAttribute('aria-expanded', 'false'); });
  $$('[data-nav-toggle]').forEach((btn) => {
    const li = btn.closest('.nav__item');
    let t;
    const open = () => { clearTimeout(t); closeNav(); li.classList.add('is-open'); btn.setAttribute('aria-expanded', 'true'); };
    btn.addEventListener('click', () => (li.classList.contains('is-open') ? closeNav() : open()));
    if (matchMedia('(hover: hover)').matches) {
      li.addEventListener('mouseenter', open);
      li.addEventListener('mouseleave', () => { t = setTimeout(closeNav, 180); });
    }
  });
  document.addEventListener('click', (e) => { if (!e.target.closest('.nav__item')) closeNav(); });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') { closeNav(); closeSearch(); } });

  /* ---------- Search panel + predictive search ---------- */
  const panel = $('[data-search-panel]');
  const closeSearch = () => { if (panel && !panel.hidden) panel.hidden = true; };
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-search-open]')) {
      e.preventDefault();
      if (!panel) return;
      panel.hidden = !panel.hidden;
      if (!panel.hidden) setTimeout(() => $('[data-predictive-input]', panel).focus(), 30);
      return;
    }
    if (e.target.closest('[data-search-close]')) closeSearch();
  });
  const pInput = $('[data-predictive-input]');
  const pOut = $('[data-predictive-results]');
  if (pInput && pOut) {
    let timer, ctrl;
    pInput.addEventListener('input', () => {
      clearTimeout(timer);
      const q = pInput.value.trim();
      if (q.length < 2) { pOut.innerHTML = ''; return; }
      timer = setTimeout(async () => {
        try {
          if (ctrl) ctrl.abort();
          ctrl = new AbortController();
          const url = `${W.routes.predictiveSearch}.json?q=${encodeURIComponent(q)}&resources[type]=product,collection&resources[limit]=6&resources[options][unavailable_products]=last`;
          const res = await fetch(url, { signal: ctrl.signal });
          const data = await res.json();
          const products = (data.resources && data.resources.results.products) || [];
          const colls = (data.resources && data.resources.results.collections) || [];
          if (!products.length && !colls.length) {
            pOut.innerHTML = `<p class="muted small">No matches for “${escapeHtml(q)}”.</p>`;
            return;
          }
          const ph = '<span class="ph-shoe"><svg viewBox="0 0 400 260" fill="none" stroke="currentColor" stroke-width="10"><path d="M38 186c-6-22 4-40 22-48 22-10 44-14 62-30 14-12 22-32 40-38 10-3 18 2 22 10 10 20 30 34 56 40 40 9 86 12 110 32 14 12 16 28 8 38H54c-8 0-14-2-16-4Z"/></svg></span>';
          pOut.innerHTML = `<div class="predictive__list" role="list">${
            colls.slice(0, 2).map((c) => `<a role="listitem" class="predictive__item" href="${c.url}"><span class="predictive__thumb">⌕</span><span><strong>${escapeHtml(c.title)}</strong><br><span class="small muted">Collection</span></span></a>`).join('')
          }${
            products.map((p) => `<a role="listitem" class="predictive__item" href="${p.url}"><span class="predictive__thumb">${p.featured_image && p.featured_image.url ? `<img src="${p.featured_image.url}&width=120" alt="" loading="lazy">` : ph}</span><span><strong>${escapeHtml(p.title)}</strong><br><span class="small muted">${formatMoney(Math.round(parseFloat(p.price) * 100))}</span></span></a>`).join('')
          }</div><a class="link predictive__all" href="${W.routes.search}?q=${encodeURIComponent(q)}&type=product">See all results</a>`;
        } catch (err) { if (err.name !== 'AbortError') pOut.innerHTML = ''; }
      }, 220);
    });
  }
  function escapeHtml(s) { return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])); }

  /* ---------- Quantity steppers ---------- */
  document.addEventListener('click', (e) => {
    const minus = e.target.closest('[data-qty-minus]');
    const plus = e.target.closest('[data-qty-plus]');
    if (!minus && !plus) return;
    const wrap = e.target.closest('[data-qty]');
    const input = $('input', wrap);
    const min = parseInt(input.min || '0', 10);
    let v = parseInt(input.value || '0', 10) + (plus ? 1 : -1);
    if (v < min) v = min;
    input.value = v;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- Cart ---------- */
  const cartSections = () => $$('[data-cart-root]').map((el) => el.dataset.section).filter(Boolean);

  const renderCart = (sections) => {
    if (!sections) return;
    Object.entries(sections).forEach(([id, html]) => {
      if (!html) return;
      const current = document.querySelector(`[data-cart-root][data-section="${id}"]`);
      if (!current) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-cart-root]');
      if (!fresh) return;
      if (current.classList.contains('drawer')) {
        // keep the drawer open state; swap only the panel contents
        $('.drawer__panel', current).innerHTML = $('.drawer__panel', fresh).innerHTML;
      } else {
        current.innerHTML = fresh.innerHTML;
      }
    });
  };

  const updateCount = (count) => {
    $$('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      if (el.classList.contains('cart-bubble')) {
        el.classList.toggle('is-empty', count === 0);
        el.classList.remove('is-bump'); void el.offsetWidth; el.classList.add('is-bump');
      }
    });
    $$('.header__cart').forEach((a) => a.setAttribute('aria-label', `Bag, ${count} items`));
  };

  const refreshCount = async () => {
    try { const c = await (await fetch(`${W.routes.cart}.js`)).json(); updateCount(c.item_count); return c; } catch (e) { return null; }
  };

  const addToCart = async (items) => {
    const ids = cartSections();
    const res = await fetch(`${W.routes.cartAdd}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: ids.join(','), sections_url: window.location.pathname })
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || W.strings.error);
    renderCart(data.sections);
    await refreshCount();
    return data;
  };

  const changeLine = async (key, quantity) => {
    const ids = cartSections();
    const lineEl = document.querySelector(`.cart-line[data-key="${CSS.escape(key)}"]`);
    $$(`.cart-line[data-key="${CSS.escape(key)}"]`).forEach((el) => el.classList.add('is-updating'));
    try {
      const res = await fetch(`${W.routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity, sections: ids.join(','), sections_url: window.location.pathname })
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || W.strings.error);
      renderCart(data.sections);
      updateCount(data.item_count);
    } catch (err) {
      toast(err.message);
      if (lineEl) lineEl.classList.remove('is-updating');
      const c = await refreshCount();
      if (c) renderCart(null);
    }
  };

  const openCart = () => {
    if (W.cartType === 'drawer' && document.getElementById('CartDrawer')) openDrawer('CartDrawer');
    else window.location.href = W.routes.cart;
  };

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-cart-open]');
    if (opener && document.getElementById('CartDrawer') && W.cartType === 'drawer') { e.preventDefault(); openDrawer('CartDrawer'); return; }
    const rm = e.target.closest('[data-cart-remove]');
    if (rm) { e.preventDefault(); changeLine(rm.dataset.cartRemove, 0); }
  });
  document.addEventListener('change', (e) => {
    const q = e.target.closest('[data-cart-qty]');
    if (q) {
      const v = Math.max(0, parseInt(q.value || '0', 10));
      changeLine(q.dataset.cartQty, v);
    }
    const note = e.target.closest('[data-cart-note]');
    if (note) {
      fetch(`${W.routes.cartUpdate}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: note.value }) });
    }
  });
  // Add-on recommendation forms (drawer + cart page)
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-rec-form]');
    if (!form) return;
    e.preventDefault();
    const btn = $('button[type=submit]', form);
    const id = parseInt(new FormData(form).get('id'), 10);
    btn.classList.add('is-loading');
    try { await addToCart([{ id, quantity: 1 }]); toast(`${W.strings.added} ✓`); }
    catch (err) { toast(err.message); }
    finally { btn.classList.remove('is-loading'); }
  });

  /* ---------- Product page ---------- */
  $$('[data-product-root]').forEach((root) => {
    const jsonEl = $('[data-product-json]', root);
    if (!jsonEl) return;
    const P = JSON.parse(jsonEl.textContent);
    const form = $('[data-product-form]', root);
    const idInput = $('[data-variant-id]', root);
    const atc = $('[data-atc]', root);
    const atcLabel = $('[data-atc-label]', root);
    const errEl = $('[data-form-error]', root);
    const fieldsets = $$('.opt', root);
    const priceWrap = $('[data-price-wrap]', root);
    const stock = $('[data-stock]', root);
    const sticky = $('[data-sticky-buy]', root);
    const stickyBtn = $('[data-sticky-atc]', root);
    const stickyMeta = $('[data-sticky-meta]', root);
    const optionCount = fieldsets.length;

    const selected = () => fieldsets.map((fs) => { const c = $('input:checked', fs); return c ? c.value : null; });
    const findVariant = (opts) => P.variants.find((v) => v.options.every((o, i) => o === opts[i]));

    const priceHtml = (v) => {
      if (v.compare > v.price) {
        return `<div class="price price--lg price--sale" data-price><span class="visually-hidden">Sale price</span><span class="price__now">${formatMoney(v.price)}</span><span class="visually-hidden">Regular price</span><s class="price__was">${formatMoney(v.compare)}</s><span class="price__save">Save ${formatMoney(v.compare - v.price)}</span></div>`;
      }
      return `<div class="price price--lg" data-price><span class="price__now">${formatMoney(v.price)}</span></div>`;
    };

    // Mark each option value as available / sold out / non-existent, given the other selections.
    const paintAvailability = () => {
      const sel = selected();
      fieldsets.forEach((fs, idx) => {
        $$('input[data-option-input]', fs).forEach((input) => {
          const label = input.nextElementSibling;
          const test = sel.slice();
          test[idx] = input.value;
          const candidates = P.variants.filter((v) => v.options.every((o, i) => i === idx ? o === input.value : (test[i] == null || o === test[i])));
          const exists = candidates.length > 0;
          const avail = candidates.some((v) => v.available);
          label.classList.toggle('is-missing', !exists && idx === P.sizeIndex);
          label.classList.toggle('is-soldout', exists && !avail);
          label.title = `${input.value}${exists && !avail ? ' – sold out' : !exists ? ' – not available in this colour' : ''}`;
        });
      });
    };

    const update = (fromUser) => {
      const sel = selected();
      fieldsets.forEach((fs, idx) => {
        const lab = $('[data-option-label]', fs);
        if (lab) lab.innerHTML = sel[idx] ? escapeHtml(sel[idx]) : '<span class="muted">select</span>';
      });
      paintAvailability();
      const complete = sel.every(Boolean);
      const v = complete ? findVariant(sel) : null;

      // Price follows colour even before size is chosen
      const ref = v || P.variants.find((x) => P.colourIndex < 0 || x.options[P.colourIndex] === sel[P.colourIndex]) || P.variants[0];
      if (priceWrap) priceWrap.innerHTML = priceHtml(ref);
      updateBundlePrices(ref);

      if (!complete) {
        idInput.value = '';
        atc.disabled = false;
        atcLabel.textContent = W.strings.chooseSize;
        if (stickyBtn) stickyBtn.textContent = W.strings.chooseSize;
      } else if (!v) {
        idInput.value = '';
        atc.disabled = true;
        atcLabel.textContent = W.strings.unavailable;
        if (stickyBtn) stickyBtn.textContent = W.strings.unavailable;
      } else {
        idInput.value = v.id;
        atc.disabled = !v.available;
        atcLabel.textContent = v.available ? `${W.strings.addToCart} · ${formatMoney(v.price * qty())}` : W.strings.soldOut;
        if (stickyBtn) stickyBtn.textContent = v.available ? W.strings.addToCart : W.strings.soldOut;
        if (stock) stock.innerHTML = v.available ? `<span class="dot dot--ok"></span>${stock.dataset.inStock || 'In stock'}` : '<span class="dot dot--no"></span>Sold out in this size';
        if (fromUser) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', v.id);
          history.replaceState({}, '', url.toString());
        }
      }
      if (stickyMeta) stickyMeta.textContent = [sel.filter(Boolean).join(' · '), formatMoney(ref.price)].filter(Boolean).join(' — ');
      // Jump gallery to the variant/colour image
      const mediaVariant = v || ref;
      if (fromUser && mediaVariant && mediaVariant.media) showMedia(mediaVariant.media);
    };

    if (stock) stock.dataset.inStock = (stock.textContent || '').trim() || 'In stock';
    const qtyInput = $('[data-pdp-qty]', root);
    const qty = () => Math.max(1, parseInt((qtyInput && qtyInput.value) || '1', 10));
    if (qtyInput) qtyInput.addEventListener('change', () => update(false));

    fieldsets.forEach((fs) => fs.addEventListener('change', (e) => {
      if (!e.target.matches('[data-option-input]')) return;
      fs.classList.remove('is-error');
      if (errEl) errEl.hidden = true;
      update(true);
    }));

    /* Gallery */
    const track = $('[data-gallery-track]', root);
    const showMedia = (mediaId) => {
      const item = track && $(`[data-media-id="${mediaId}"]`, track);
      if (!item) return;
      if (matchMedia('(min-width: 990px)').matches) {
        // move chosen image to the first (large) slot
        track.prepend(item);
      } else {
        track.scrollTo({ left: item.offsetLeft - track.offsetLeft, behavior: 'smooth' });
      }
    };
    if (track) {
      const dots = $$('.gallery__dots span', root);
      track.addEventListener('scroll', () => {
        const i = Math.round(track.scrollLeft / track.clientWidth);
        dots.forEach((d, n) => d.classList.toggle('is-active', n === i));
      }, { passive: true });
      $$('[data-thumb]', root).forEach((b) => b.addEventListener('click', () => showMedia(b.dataset.thumb)));
    }

    /* Two-pair option */
    const bundle = $('[data-bundle]', root);
    const second = bundle && $('[data-bundle-second]', bundle);
    const bundleQty = () => (bundle ? parseInt(($('[data-bundle-qty]:checked', bundle) || {}).value || '1', 10) : 1);
    function updateBundlePrices(ref) {
      if (!bundle || !ref) return;
      const one = $('[data-bundle-one]', bundle), two = $('[data-bundle-two]', bundle);
      if (one) one.textContent = formatMoney(ref.price);
      if (two) two.textContent = formatMoney(ref.price * 2);
    }
    if (bundle) {
      bundle.addEventListener('change', (e) => {
        if (e.target.matches('[data-bundle-qty]')) {
          second.hidden = bundleQty() !== 2;
          if (!second.hidden) {
            // default pair 2 colour to the current colour for convenience
            const sel = selected();
            $$('[data-bundle-option]', second).forEach((s) => {
              const i = parseInt(s.dataset.bundleOption, 10);
              if (i !== P.sizeIndex && sel[i]) s.value = sel[i];
            });
          }
        }
      });
    }
    const secondVariant = () => {
      const opts = $$('[data-bundle-option]', second).sort((a, b) => a.dataset.bundleOption - b.dataset.bundleOption).map((s) => s.value || null);
      if (!opts.every(Boolean)) return { error: 'Choose a size for pair 2' };
      const v = findVariant(opts);
      if (!v) return { error: 'Pair 2: this colour and size combination is not available' };
      if (!v.available) return { error: 'Pair 2: this colour and size is sold out' };
      return { v };
    };

    const showError = (msg) => { if (errEl) { errEl.textContent = msg; errEl.hidden = false; } };

    const submit = async () => {
      const sel = selected();
      if (P.singleVariant) sel.length = 0;
      const missing = fieldsets.findIndex((fs) => !$('input:checked', fs));
      if (missing > -1) {
        const fs = fieldsets[missing];
        fs.classList.remove('is-error'); void fs.offsetWidth; fs.classList.add('is-error');
        const lab = $('[data-option-label]', fs);
        if (lab) lab.innerHTML = `<span class="opt__err">${missing === P.sizeIndex ? 'please choose a size' : 'please choose'}</span>`;
        fs.scrollIntoView({ behavior: 'smooth', block: 'center' });
        showError(missing === P.sizeIndex ? 'Please choose your size first.' : 'Please choose an option.');
        return;
      }
      const vid = parseInt(idInput.value || (P.singleVariant ? P.variants[0].id : 0), 10);
      if (!vid) { showError('This combination is not available.'); return; }
      const items = [{ id: vid, quantity: qty() }];
      if (bundle && bundleQty() === 2) {
        const s2 = secondVariant();
        if (s2.error) { showError(s2.error); second.scrollIntoView({ behavior: 'smooth', block: 'center' }); return; }
        if (s2.v.id === vid) items[0].quantity += 1; else items.push({ id: s2.v.id, quantity: 1 });
      }
      $$('[data-addon]', root).forEach((a) => {
        if ($('[data-addon-toggle]', a).checked) items.push({ id: parseInt($('[data-addon-variant]', a).value, 10), quantity: 1 });
      });
      atc.classList.add('is-loading');
      try {
        await addToCart(items);
        $$('[data-addon-toggle]', root).forEach((c) => { c.checked = false; });
        openCart();
      } catch (err) {
        showError(err.message);
      } finally {
        atc.classList.remove('is-loading');
      }
    };

    form.addEventListener('submit', (e) => { e.preventDefault(); submit(); });

    /* Sticky bar: appears when the main button leaves the viewport */
    if (sticky && 'IntersectionObserver' in window) {
      sticky.hidden = false;
      document.body.classList.add('has-sticky-buy');
      const io = new IntersectionObserver(([entry]) => {
        const past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        sticky.classList.toggle('is-visible', past);
        sticky.setAttribute('aria-hidden', past ? 'false' : 'true');
      });
      io.observe(atc);
      stickyBtn.addEventListener('click', () => {
        const sel = selected();
        if (!sel.every(Boolean) || (bundle && bundleQty() === 2)) {
          // Never add an incomplete selection from the bar — take the customer back to the options.
          const target = fieldsets.find((fs) => !$('input:checked', fs)) || form;
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
          if (!sel.every(Boolean)) setTimeout(submit, 450);
          return;
        }
        submit();
      });
    }

    update(false);
  });

  /* ---------- Recommendations ---------- */
  $$('[data-recommendations]').forEach(async (el) => {
    if (el.children.length && el.querySelector('.grid')) return;
    try {
      const html = await (await fetch(el.dataset.url)).text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-recommendations]');
      if (fresh && fresh.innerHTML.trim()) el.innerHTML = fresh.innerHTML;
    } catch (e) { /* silent */ }
  });

  /* ---------- Collection filters & sort ---------- */
  const facetsForm = $('[data-facets-form]');
  const desktopSlot = facetsForm ? facetsForm.parentElement : null;
  function moveFacets(toDrawer) {
    const body = $('[data-filter-drawer-body]');
    if (!facetsForm || !body) return;
    if (toDrawer) body.appendChild(facetsForm); else if (desktopSlot) desktopSlot.appendChild(facetsForm);
  }
  if (facetsForm) {
    let t;
    const apply = () => {
      const params = new URLSearchParams(new FormData(facetsForm));
      [...params.keys()].forEach((k) => { if (params.get(k) === '') params.delete(k); });
      window.location.search = params.toString();
    };
    facetsForm.addEventListener('change', (e) => {
      if (!e.target.matches('[data-facet-input]')) return;
      clearTimeout(t);
      t = setTimeout(apply, e.target.type === 'number' ? 700 : 150);
    });
    facetsForm.addEventListener('submit', (e) => { e.preventDefault(); apply(); });
  }
  $$('[data-sort]').forEach((sel) => sel.addEventListener('change', () => {
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', sel.value);
    url.searchParams.delete('page');
    window.location.href = url.toString();
  }));

  /* ---------- Subtle reveal ---------- */
  if ('IntersectionObserver' in window && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
    const els = $$('.section .section-head, .section .grid > li, .cat-grid > li, .feature, .service');
    const io = new IntersectionObserver((entries) => entries.forEach((en) => {
      if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); }
    }), { rootMargin: '0px 0px -40px 0px' });
    els.forEach((el, i) => { el.classList.add('reveal'); el.style.transitionDelay = `${(i % 4) * 60}ms`; io.observe(el); });
  }
})();
